import React, { FC } from 'react';
import type { WebWorkKindStats, WebWorkReasonStats } from './types';
import { fmtInt, fmtPct } from './format';

/**
 * "Why it became human work" — hand-off reasons ranked by frequency, with the
 * top kinds each reason shows up under (derived from the per-kind breakdown)
 * and the top sites recorded for rows with that reason.
 */

export interface HandoffReasonsPanelProps {
  reasons: WebWorkReasonStats[];
  kinds: WebWorkKindStats[];
}

const TOP_N = 3;

export function topKindsForReason(
  reason: string,
  kinds: WebWorkKindStats[],
): string[] {
  return kinds
    .map((k) => ({
      label: k.label,
      count: k.reasons.find((r) => r.reason === reason)?.count ?? 0,
    }))
    .filter((k) => k.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_N)
    .map((k) => `${k.label} (${fmtInt(k.count)})`);
}

export function topSitesForReason(reason: WebWorkReasonStats): string[] {
  return reason.domains
    .filter((d) => d.domain !== 'unknown')
    .slice(0, TOP_N)
    .map((d) => d.domain);
}

export const HandoffReasonsPanel: FC<HandoffReasonsPanelProps> = ({
  reasons,
  kinds,
}) => {
  if (reasons.length === 0) {
    return <p className='wwm-empty'>No hand-off reasons recorded yet.</p>;
  }
  return (
    <div className='wwm-table-wrap'>
      <table className='wwm-table' data-testid='reasons-table'>
        <thead>
          <tr>
            <th>Reason</th>
            <th className='num'>Count</th>
            <th className='num'>Share</th>
            <th>Top kinds</th>
            <th>Top sites</th>
          </tr>
        </thead>
        <tbody>
          {reasons.map((r) => {
            const topKinds = topKindsForReason(r.reason, kinds);
            const topSites = topSitesForReason(r);
            return (
              <tr key={r.reason} data-testid={`reason-row-${r.reason}`}>
                <td>{r.label}</td>
                <td className='num'>{fmtInt(r.count)}</td>
                <td className='num'>{fmtPct(r.sharePct)}</td>
                <td className='wwm-muted'>
                  {topKinds.length ? topKinds.join(' · ') : '—'}
                </td>
                <td className='wwm-muted'>
                  {topSites.length ? topSites.join(' · ') : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className='wwm-note'>
        Structured reasons already recorded by the system (ladder blocked
        reason, browser fallback reason, card entry, credentials, CAPTCHA
        classification) win over the classifier. Top kinds/sites are the kinds
        and sites recorded on the rows with this reason.
      </p>
    </div>
  );
};
